// src/automation/components/KycReportDesignBlock.js
//
// Report Design for KYC Core. KYC sends TWO emails (T1/T2 and T3/T4); each is a
// list of tables, and each table groups one or more functions from the mapping.
// The editor lets an admin, without touching code:
//   • retitle / reorder / add / delete tables in each email
//   • move a function to another table (or email), add unassigned functions
//   • set the column (language) order per table — KYC tables differ (AWS uses
//     tiers, APAY uses product types), so the order is per table, not global
//   • edit each email's greeting, the shared sign-off, and the summary rows
//
// The starting layout comes from the backend (`report_layout`): config tables
// if saved, else the mapping's report/report_table columns. Saving writes a
// `report` block into the task config. Tables are only written once you change
// them, so an untouched layout keeps following the mapping.

import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  RefreshCw, Save, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, X, Plus, AlertTriangle, Info, Table2,
} from "lucide-react";
import { getReportLayout } from "../api/automation_data";

// Emails in the report. The backend sends the list (`report_list`) so other
// KYC-style reports (e.g. Tax: one email) reuse this editor; KYC Core is the default.
const DEFAULT_REPORTS = [
  ["T1_T2", "T1/T2 email"],
  ["T3_T4", "T3/T4 email"],
];
const reportsOf = (layout) =>
  Array.isArray(layout?.report_list) && layout.report_list.length ? layout.report_list : DEFAULT_REPORTS;
const TOP_ROWS = [
  ["total_volume", "Total Vol"],
  ["max_age", "Max Age"],
  ["insl", "INSL"],
  ["oosl", "OOSL"],
];
const PLACEHOLDERS = ["{date}", "{queue_count}", "{total_volume}", "{tables}"];

function move(list, idx, dir) {
  const j = idx + dir;
  if (j < 0 || j >= list.length) return list;
  const copy = [...list];
  [copy[idx], copy[j]] = [copy[j], copy[idx]];
  return copy;
}

// Build the editable draft from the backend layout + the saved config.
function fromLayout(layout, config) {
  const rep = config?.report || {};
  const cfgReports = rep.reports || {};
  const reports = {};
  const greetings = {};
  for (const [R] of reportsOf(layout)) {
    greetings[R] = cfgReports[R]?.greeting ?? "";
    const cfgTables = Array.isArray(cfgReports[R]?.tables) ? cfgReports[R].tables : [];
    reports[R] = (layout.reports?.[R] || []).map((t) => {
      const saved = cfgTables.find((c) => c && c.key === t.key);
      return {
        key: t.key,
        title: t.title || "",
        defaultTitle: t.default_title,
        functions: [...t.functions],
        languages: [...(t.languages || [])],
        // Keep an explicitly saved language order; otherwise it stays default.
        langTouched: Array.isArray(saved?.languages),
      };
    });
  }
  return {
    greetings,
    signoff: rep.signoff ?? "",
    rows: { total_volume: true, max_age: true, insl: true, oosl: true, ...(rep.rows || {}) },
    reports,
    tablesDirty: false,
    resetTables: false,
  };
}

export default function KycReportDesignBlock({ taskName, config, canEdit, onSave }) {
  const { data: layout, isLoading, error, refetch } = useQuery({
    queryKey: ["reportLayout", taskName],
    queryFn: () => getReportLayout(taskName),
    enabled: !!taskName,
    staleTime: 5 * 60 * 1000,
  });

  const [draft, setDraft] = useState(null);
  const [activeTab, setActive] = useState(null);
  const REPORTS = reportsOf(layout);
  const active = activeTab || REPORTS[0][0];
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (layout && !draft) setDraft(fromLayout(layout, config));
  }, [layout, draft, config]);

  // Function inventory: name -> { queues, languages }.
  const inventory = useMemo(() => {
    const m = {};
    for (const f of layout?.functions || []) m[f.name] = f;
    return m;
  }, [layout]);

  if (isLoading) return <div className="auto-empty">Loading report layout…</div>;
  if (error) {
    return (
      <div className="auto-eb-alert auto-eb-alert--err">
        <AlertTriangle size={13} /> <span>{error.message}</span>
      </div>
    );
  }
  if (!draft) return null;

  const readOnly = !canEdit;
  const tables = draft.reports[active];
  const allTables = REPORTS.flatMap(([R, label]) =>
    draft.reports[R].map((t, i) => ({ R, i, label: `${label.replace(" email", "")} · ${t.title || t.defaultTitle}` }))
  );
  const assigned = new Set(REPORTS.flatMap(([R]) => draft.reports[R].flatMap((t) => t.functions)));
  const unassigned = Object.keys(inventory).filter((f) => !assigned.has(f)).sort();

  // ── table helpers ──
  const tableLanguages = (t) => {
    const avail = new Set(t.functions.flatMap((f) => inventory[f]?.languages || []));
    const ordered = t.languages.filter((l) => avail.has(l));
    const rest = [...avail].filter((l) => !ordered.includes(l)).sort();
    return [...ordered, ...rest];
  };
  const tableQueues = (t) => t.functions.reduce((n, f) => n + (inventory[f]?.queues || 0), 0);

  const updateReports = (fn) =>
    setDraft((d) => ({ ...d, reports: fn(d.reports), tablesDirty: true, resetTables: false }));
  const updateTable = (R, i, patch) =>
    updateReports((reps) => ({
      ...reps,
      [R]: reps[R].map((t, j) => (j === i ? { ...t, ...(typeof patch === "function" ? patch(t) : patch) } : t)),
    }));

  const addTable = (R) =>
    updateReports((reps) => {
      const used = new Set(REPORTS.flatMap(([X]) => reps[X].map((t) => t.key)));
      let n = 1;
      while (used.has(`custom_${n}`)) n += 1;
      return {
        ...reps,
        [R]: [...reps[R], {
          key: `custom_${n}`, title: "New table", defaultTitle: `CUSTOM ${n}`,
          functions: [], languages: [], langTouched: false,
        }],
      };
    });
  const deleteTable = (R, i) => updateReports((reps) => ({ ...reps, [R]: reps[R].filter((_, j) => j !== i) }));
  const moveTable = (R, i, dir) => updateReports((reps) => ({ ...reps, [R]: move(reps[R], i, dir) }));

  const removeFunction = (R, i, f) =>
    updateTable(R, i, (t) => ({ functions: t.functions.filter((x) => x !== f) }));
  const addFunction = (R, i, f) =>
    f && updateTable(R, i, (t) => ({ functions: t.functions.includes(f) ? t.functions : [...t.functions, f] }));
  const relocateFunction = (f, toR, toI) =>
    updateReports((reps) => {
      const next = {};
      for (const [R] of REPORTS) {
        next[R] = reps[R].map((t, j) => {
          const without = t.functions.filter((x) => x !== f);
          return R === toR && j === toI ? { ...t, functions: [...without, f] } : { ...t, functions: without };
        });
      }
      return next;
    });
  const moveLanguage = (R, i, li, dir) =>
    updateTable(R, i, (t) => ({ languages: move(tableLanguages(t), li, dir), langTouched: true }));

  // ── save ──
  const buildReport = () => {
    const report = {};
    if (draft.signoff.trim()) report.signoff = draft.signoff.trim();
    if (TOP_ROWS.some(([k]) => draft.rows[k] === false)) report.rows = { ...draft.rows };
    const reports = {};
    for (const [R] of REPORTS) {
      const obj = {};
      if (draft.greetings[R].trim()) obj.greeting = draft.greetings[R].trim();
      const writeTables =
        !draft.resetTables && (draft.tablesDirty || layout.sources?.[R] === "config");
      if (writeTables) {
        obj.tables = draft.reports[R]
          .filter((t) => t.functions.length)
          .map((t) => ({
            key: t.key,
            ...(t.title.trim() ? { title: t.title.trim() } : {}),
            functions: t.functions,
            ...(t.langTouched ? { languages: tableLanguages(t) } : {}),
          }));
      }
      if (Object.keys(obj).length) reports[R] = obj;
    }
    if (Object.keys(reports).length) report.reports = reports;
    return report;
  };

  const handleSave = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const report = buildReport();
      const next = { ...config };
      if (Object.keys(report).length) next.report = report;
      else delete next.report;
      await onSave(next);
      const fresh = await refetch();
      if (fresh.data) setDraft(fromLayout(fresh.data, next));
      setNotice({ type: "ok", msg: "Report design saved. Run a Test to preview the email(s)." });
    } catch (e) {
      setNotice({ type: "err", msg: e?.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft((d) => ({ ...d, resetTables: true, tablesDirty: false }));
    setNotice({ type: "ok", msg: "Tables will follow the mapping again after you click Save." });
  };

  return (
    <div className="auto-rd">
      {notice && (
        <div className={`auto-eb-alert ${notice.type === "err" ? "auto-eb-alert--err" : "auto-eb-alert--ok"}`}>
          <span>{notice.msg}</span>
        </div>
      )}
      {!layout.routing_columns && (
        <div className="auto-rd-warn">
          <Info size={13} />
          <span>
            The mapping in S3 has no report/report_table columns yet, so tables come from the
            built-in defaults. Upload the updated kyc_queues.csv to make the mapping drive the layout.
          </span>
        </div>
      )}

      {/* ── Email text ── */}
      <section className="auto-rd-sec">
        <div className="auto-rd-sechead">
          <h5 className="auto-rd-h">Email text</h5>
          <span className="auto-rd-hint">Blank = default · placeholders: {PLACEHOLDERS.join(" ")}</span>
        </div>
        <div className="auto-rd-grid2">
          {REPORTS.map(([R, label]) => (
            <label className="auto-rd-field" key={R}>
              <span>{label} greeting</span>
              <input
                className="auto-eb-input" disabled={readOnly} value={draft.greetings[R]}
                placeholder={(layout.defaults?.greetings?.[R] || "").replace("<br>", " ")}
                onChange={(e) => setDraft((d) => ({ ...d, greetings: { ...d.greetings, [R]: e.target.value } }))}
              />
            </label>
          ))}
          <label className="auto-rd-field">
            <span>Sign-off{REPORTS.length > 1 ? " (all emails)" : ""}</span>
            <input
              className="auto-eb-input" disabled={readOnly} value={draft.signoff}
              placeholder={layout.defaults?.signoff || "Regards,<br>GCC Automation"}
              onChange={(e) => setDraft((d) => ({ ...d, signoff: e.target.value }))}
            />
          </label>
          <div className="auto-rd-field">
            <span>Summary rows (every table)</span>
            <div className="auto-rd-toggles">
              {TOP_ROWS.map(([k, lbl]) => (
                <label key={k} className="auto-eb-toggle">
                  <input
                    type="checkbox" disabled={readOnly} checked={draft.rows[k] !== false}
                    onChange={(e) => setDraft((d) => ({ ...d, rows: { ...d.rows, [k]: e.target.checked } }))}
                  />
                  <span>{lbl}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Tables per email ── */}
      <section className="auto-rd-sec">
        <div className="auto-subtabs">
          {REPORTS.map(([R, label]) => (
            <button
              key={R}
              className={`auto-subtab ${active === R ? "auto-subtab--active" : ""}`}
              onClick={() => setActive(R)}
            >
              {label} ({draft.reports[R].length})
            </button>
          ))}
        </div>
        <div className="auto-rd-sechead">
          <span className="auto-rd-hint">
            Source: {draft.resetTables ? "mapping (after save)" : (layout.sources?.[active] || "mapping")}
            {draft.tablesDirty ? " · unsaved changes" : ""}
          </span>
          {!readOnly && (
            <button className="auto-rd-chip" onClick={() => addTable(active)}><Plus size={11} /> Add table</button>
          )}
        </div>

        {tables.map((t, i) => {
          const langs = tableLanguages(t);
          return (
            <div className="auto-rd-table" key={`${active}-${t.key}`}>
              <div className="auto-rd-table-head">
                <Table2 size={14} className="auto-rd-table-ic" />
                <input
                  className="auto-eb-input auto-rd-title-in" disabled={readOnly}
                  value={t.title} placeholder={t.defaultTitle}
                  onChange={(e) => updateTable(active, i, { title: e.target.value })}
                />
                <span className="auto-kyc-key" title="Table key">{t.key}</span>
                <span className="auto-rd-count">{t.functions.length} fn · {tableQueues(t)} q</span>
                {!readOnly && (
                  <span className="auto-rd-actions">
                    <button className="auto-rd-ic" title="Up" disabled={i === 0}
                      onClick={() => moveTable(active, i, -1)}><ArrowUp size={12} /></button>
                    <button className="auto-rd-ic" title="Down" disabled={i === tables.length - 1}
                      onClick={() => moveTable(active, i, 1)}><ArrowDown size={12} /></button>
                    <button className="auto-rd-ic auto-rd-ic--del" title="Delete table (functions become unassigned)"
                      onClick={() => deleteTable(active, i)}><X size={12} /></button>
                  </span>
                )}
              </div>

              {t.functions.length === 0 ? (
                <div className="auto-rd-empty-table">No functions — add one below (empty tables are not sent).</div>
              ) : (
                <div className="auto-kyc-fns">
                  {t.functions.map((f) => (
                    <span key={f} className={`auto-kyc-fn ${inventory[f] ? "" : "auto-kyc-fn--bad"}`}>
                      <span className="auto-kyc-fn-name" title={f}>{f}</span>
                      <span className="auto-kyc-fn-q">{inventory[f]?.queues ?? 0}</span>
                      {!readOnly && (
                        <>
                          <select
                            className="auto-kyc-fn-sel" title="Move to table" value={`${active}|${i}`}
                            onChange={(e) => {
                              const [R, idx] = e.target.value.split("|");
                              relocateFunction(f, R, Number(idx));
                            }}
                          >
                            {allTables.map((o) => (
                              <option key={`${o.R}|${o.i}`} value={`${o.R}|${o.i}`}>{o.label}</option>
                            ))}
                          </select>
                          <button className="auto-rd-ic auto-rd-ic--del" title="Remove from table"
                            onClick={() => removeFunction(active, i, f)}><X size={10} /></button>
                        </>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {langs.length > 1 && (
                <div className="auto-kyc-langs">
                  <span className="auto-rd-hint">Columns:</span>
                  {langs.map((l, li) => (
                    <span key={l} className="auto-rd-lang">
                      {!readOnly && (
                        <button className="auto-rd-ic" disabled={li === 0}
                          onClick={() => moveLanguage(active, i, li, -1)}><ArrowLeft size={10} /></button>
                      )}
                      <span>{l}</span>
                      {!readOnly && (
                        <button className="auto-rd-ic" disabled={li === langs.length - 1}
                          onClick={() => moveLanguage(active, i, li, 1)}><ArrowRight size={10} /></button>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {!readOnly && unassigned.length > 0 && (
                <div className="auto-rd-add">
                  <span>Add:</span>
                  <select className="auto-kyc-fn-sel" value="" onChange={(e) => addFunction(active, i, e.target.value)}>
                    <option value="">unassigned function…</option>
                    {unassigned.map((f) => (
                      <option key={f} value={f}>{f} ({inventory[f]?.queues ?? 0} q)</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}

        {unassigned.length > 0 && (
          <div className="auto-rd-warn">
            <Info size={13} />
            <span>{unassigned.length} function(s) are in no table and won't be reported: {unassigned.join(", ")}</span>
          </div>
        )}
      </section>

      {!readOnly && (
        <div className="auto-rd-footer">
          <button className="auto-eb-btn" onClick={handleReset} disabled={saving}>Reset tables to mapping</button>
          <button className="auto-eb-btn auto-eb-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <RefreshCw size={13} className="auto-eb-spin" /> : <Save size={13} />} Save report design
          </button>
        </div>
      )}
    </div>
  );
}
